import React from 'react';
import { X, Upload, FileText, RefreshCw } from 'lucide-react';
import { TemplateCategory } from './types';

interface UploadModalProps {
    onClose: () => void;
    uploadForm: {
        name: string;
        description: string;
        category_id: string;
        file: File | null;
    };
    setUploadForm: (form: any) => void;
    categories: TemplateCategory[];
    isDragging: boolean;
    uploading: boolean;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onFileUpload: (files: FileList | null) => void;
    onPerformUpload: (file: File, name: string, description: string) => void;
    onShowAddCategoryModal: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
    onClose,
    uploadForm,
    setUploadForm,
    categories,
    isDragging,
    uploading,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileUpload,
    onPerformUpload,
    onShowAddCategoryModal,
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Şablon Yükle</h2>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {!uploadForm.file ? (
                        <div
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Dosyayı buraya sürükleyin
                            </h3>
                            <p className="text-gray-500 mb-4">veya dosya seçmek için tıklayın</p>

                            <input
                                type="file"
                                accept=".docx"
                                onChange={(e) => onFileUpload(e.target.files)}
                                className="hidden"
                                id="file-upload"
                            />
                            <label
                                htmlFor="file-upload"
                                className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors cursor-pointer inline-block"
                            >
                                Dosya Seç
                            </label>

                            <div className="mt-4 text-sm text-gray-500">
                                <p>• Sadece .docx dosyaları desteklenir</p>
                                <p>• Maksimum dosya boyutu: 20MB</p>
                                <p>• Şablon alanları otomatik olarak tespit edilecek</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center">
                                    <FileText className="h-8 w-8 text-blue-600 mr-3" />
                                    <div>
                                        <p className="font-medium text-gray-900">{uploadForm.file.name}</p>
                                        <p className="text-sm text-gray-500">
                                            {Math.round(uploadForm.file.size / 1024)} KB
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Template Adı *
                                </label>
                                <input
                                    type="text"
                                    value={uploadForm.name}
                                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Template adını girin..."
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Kategori
                                </label>
                                {categories.length > 0 ? (
                                    <div className="space-y-2">
                                        <select
                                            value={uploadForm.category_id}
                                            onChange={(e) =>
                                                setUploadForm({ ...uploadForm, category_id: e.target.value })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Kategori seçin (opsiyonel)</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={onShowAddCategoryModal}
                                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                                        >
                                            + Yeni kategori ekle
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
                                            Henüz kategori oluşturulmamış. Şablon kategorisiz olarak yüklenecek.
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={onShowAddCategoryModal}
                                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                                            >
                                                + Kategori oluştur
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Açıklama
                                </label>
                                <textarea
                                    value={uploadForm.description}
                                    onChange={(e) =>
                                        setUploadForm({ ...uploadForm, description: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Template açıklaması (opsiyonel)..."
                                    rows={3}
                                />
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() =>
                                        setUploadForm({ name: '', description: '', category_id: '', file: null })
                                    }
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                    disabled={uploading}
                                >
                                    Dosya Değiştir
                                </button>
                                <button
                                    onClick={() =>
                                        onPerformUpload(
                                            uploadForm.file!,
                                            uploadForm.name,
                                            uploadForm.description
                                        )
                                    }
                                    disabled={!uploadForm.name.trim() || uploading}
                                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${uploading || !uploadForm.name.trim()
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-blue-900 text-white hover:bg-blue-800'
                                        }`}
                                >
                                    {uploading ? (
                                        <div className="flex items-center justify-center">
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            Yükleniyor...
                                        </div>
                                    ) : (
                                        'Template Yükle'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UploadModal;
